import { EntityType } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import type { IGroup } from '@frontend/group/types';
import { ICONS, Plus } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { useBlobRepository } from '@frontend/repository';
import { UserSelectV2 } from '@frontend/user/select';
import { User } from '@frontend/user/utils';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NamePromptModal } from '../../components/name-prompt-modal/name-prompt-modal.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useSelectGroupedReceivers from './select-grouped-receivers.controller';

const ID = 'select-grouped-receivers';

const SelectGroupedReceivers = () => {
    const intl = useIntl();
    const viewProps = useSelectGroupedReceivers();

    const receiverNotFoundButton =
        viewProps.showReceiverNotFoundToAllUsers || viewProps.showReceiverNotFoundModal ? (
            <button
                type='button'
                className='border-brand-primary text-brand-primary bg-background-surface mt-auto flex w-full shrink-0 items-center justify-center rounded-xl border px-4 py-3 text-base font-medium'
                onClick={() => {
                    if (viewProps.showReceiverNotFoundModal) {
                        viewProps.onReceiverNotFoundOpenModal();
                        return;
                    }
                    void viewProps.onReceiverNotFoundToAllUsers();
                }}>
                <FormattedMessage
                    id='kiosk.wizard.select-grouped-receiver.receiver-not-found'
                    defaultMessage='Receiver not found'
                    description='Button to continue when the receiver cannot be found in the grouped receiver flow'
                />
            </button>
        ) : null;

    const receiversList = (
        <>
            <p className='text-foreground-default relative w-full shrink-0 text-base leading-normal font-medium'>
                <FormattedMessage
                    id='Wizard.SelectReceivers.Instructions'
                    defaultMessage='Please select the receiver(s)'
                    description='Instructions for receiver selection'
                />
            </p>
            {viewProps.view === 'groupUsers' && viewProps.selectedGroup ? (
                <div className='bg-background-surface border-border-default flex w-full shrink-0 items-center gap-4 rounded-xl border border-solid px-4 py-3'>
                    <div className='flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden'>
                        <GroupImage
                            group={viewProps.selectedGroup}
                            compact
                        />
                    </div>
                    <p className='text-foreground-default text-base leading-normal font-medium break-words'>{viewProps.selectedGroup.name}</p>
                </div>
            ) : null}
            {viewProps.delayedReceiversSearch ? (
                <p className='text-brand-muted relative w-full shrink-0 text-sm leading-normal font-medium'>
                    <FormattedMessage
                        id='kiosk.wizard.select-receivers.delayed-search-hint'
                        defaultMessage='Type at least 3 characters to search'
                        description='Hint shown on kiosk select-receivers when delayed_receivers_search is enabled'
                    />
                </p>
            ) : null}
            {viewProps.isLoading ? (
                <Spinner />
            ) : (
                <div className='flex w-full flex-col gap-3'>
                    {viewProps.selectedReceivers.map((receiver, index) => (
                        <div
                            key={index + '-' + (receiver ? receiver : '')}
                            className='flex w-full items-center gap-3'>
                            <div className='flex-1'>
                                <UserSelectV2
                                    id={`${viewProps.view}-${index}`}
                                    hideLabel
                                    value={receiver}
                                    onChange={(user) => viewProps.updateReceiver(index, user)}
                                    isClearable
                                    {...(viewProps.delayedReceiversSearch ? { minSearchCharacters: 3 } : {})}
                                    customLabel={(user) => new User(user).getDisplayName()}
                                    fetchOverwrite={viewProps.fetchReceiverOptions}
                                    subset={viewProps.receiverSubsets[index]}
                                />
                            </div>
                            {viewProps.selectedReceivers.length > 1 && (
                                <button
                                    type='button'
                                    onClick={() => viewProps.removeReceiver(index)}
                                    className='text-foreground-default hover:text-foreground-emphasis shrink-0 transition-colors'
                                    aria-label='Remove receiver'>
                                    <ICONS.BUTTON.DELETE className='size-5' />
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        type='button'
                        onClick={viewProps.addReceiver}
                        className='flex w-full items-center justify-end gap-3'>
                        <Plus className='size-3.5' />
                        <p className='text-foreground-default text-base leading-normal font-medium'>
                            <FormattedMessage
                                id='Wizard.SelectReceivers.AddAnother'
                                defaultMessage='Add another receiver'
                                description='Button to add another receiver'
                            />
                        </p>
                    </button>
                </div>
            )}
            {receiverNotFoundButton}
        </>
    );

    return (
        <WizardScreenContainer
            id={ID}
            header={{ onBack: viewProps.showBack ? viewProps.onBack : undefined }}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: !viewProps.canContinue,
                submitLabel: CommonMessage.BUTTONS.CONTINUE
            }}
            containerClassName='relative flex h-full w-full flex-col content-stretch items-start gap-6 overflow-y-auto p-6'>
            {viewProps.view === 'groupUsers' || viewProps.view === 'allUsers' ? (
                receiversList
            ) : (
                <>
                    <p className='text-foreground-default relative w-full shrink-0 text-base leading-normal font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.select-grouped-receiver.group-instructions'
                            defaultMessage='Please select a group'
                            description='Instructions above the nested receiver group grid on select-grouped-receiver'
                        />
                    </p>
                    {viewProps.isLoading ? (
                        <Spinner />
                    ) : (
                        <div className='grid w-full grid-cols-2 gap-3'>
                            {viewProps.groups.map((group) => (
                                <button
                                    key={group.id}
                                    type='button'
                                    onClick={() => viewProps.onSelectGroup(group)}
                                    className='bg-background-surface border-border-default flex min-h-20 items-center justify-center rounded-xl border border-solid p-4 transition-colors'>
                                    <GroupImage group={group} />
                                </button>
                            ))}
                        </div>
                    )}
                    {receiverNotFoundButton}
                </>
            )}

            <NamePromptModal
                id='select-grouped-receivers-not-found-modal'
                show={viewProps.showNotFoundModal}
                value={viewProps.notFoundName}
                isSubmitting={!!viewProps.isLoading}
                title={
                    <FormattedMessage
                        id='kiosk.wizard.select-grouped-receiver.not-found-modal.title'
                        defaultMessage='Who is receiving?'
                        description='Title of the receiver-not-found name modal on select-grouped-receiver'
                    />
                }
                placeholder={intl.formatMessage({
                    id: 'kiosk.wizard.select-grouped-receiver.not-found-modal.placeholder',
                    defaultMessage: 'Name or company',
                    description: 'Placeholder for name or company on the receiver-not-found modal'
                })}
                onChange={viewProps.setNotFoundName}
                onClose={viewProps.closeNotFoundModal}
                onSubmit={viewProps.onNotFoundModalSubmit}
            />
        </WizardScreenContainer>
    );
};

export default SelectGroupedReceivers;

const GroupImage = ({ group, compact = false }: { group: IGroup; compact?: boolean }): React.ReactNode => {
    const blobRepository = useBlobRepository();
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchImage = async () => {
            try {
                setIsLoading(true);
                const blobInfo = await blobRepository.getBlob(group.account_id, EntityType.USER_GROUP, group.id);
                setImageUrl(blobInfo?.url ?? null);
            } catch (error) {
                Logger.error('Failed to fetch group image:', error);
                setImageUrl(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchImage();
    }, [group.account_id, group.id]); // eslint-disable-line react-hooks/exhaustive-deps

    let content: React.ReactNode = (
        <div className='text-always-black px-2 text-center text-sm leading-normal font-medium break-words whitespace-pre-wrap'>{group.name}</div>
    );
    if (isLoading) {
        content = <Spinner />;
    } else if (imageUrl) {
        content = (
            <img
                alt={group.name}
                className={compact ? 'h-full w-full object-contain' : 'max-h-24 w-full object-contain'}
                src={imageUrl}
            />
        );
    }

    return <div className='relative flex max-h-full max-w-full items-center justify-center'>{content}</div>;
};
